import type { Session, User } from '@supabase/supabase-js';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { MembershipRole, Organization } from '../types';
import { mapOrganization, type OrganizationRecord } from '../lib/organization';
import { isDemoMode, isSupabaseConfigured, supabase } from '../lib/supabase';

export type AccessStatus = 'loading' | 'demo' | 'signed-out' | 'needs-bootstrap' | 'pending' | 'active' | 'error';

interface ProfileRecord {
  id: string;
  display_name: string;
  username: string | null;
  avatar_url: string | null;
}

interface MembershipRecord {
  organization_id: string;
  user_id: string;
  role: MembershipRole;
  status: 'invited' | 'pending_guardian_consent' | 'active' | 'suspended' | 'left';
}

interface AppContextResponse {
  profile: ProfileRecord | null;
  membership: MembershipRecord | null;
  organization: OrganizationRecord | null;
  access_granted: boolean;
  can_bootstrap: boolean;
}

interface AuthContextValue {
  status: AccessStatus;
  user: User | null;
  profile: ProfileRecord | null;
  membership: MembershipRecord | null;
  organization: Organization | null;
  error: string | null;
  refreshContext: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const configuredOrganizationSlug = import.meta.env.VITE_ORGANIZATION_SLUG || null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AccessStatus>('loading');
  const [profile, setProfile] = useState<ProfileRecord | null>(null);
  const [membership, setMembership] = useState<MembershipRecord | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadContext = useCallback(async (nextSession: Session | null) => {
    setError(null);

    if (isDemoMode) {
      setStatus('demo');
      return;
    }
    if (!isSupabaseConfigured || !supabase || !nextSession) {
      setProfile(null);
      setMembership(null);
      setOrganization(null);
      setStatus('signed-out');
      return;
    }

    setStatus('loading');
    await supabase.rpc('claim_my_organization_invitation', {
      target_slug: configuredOrganizationSlug,
    });
    const { data, error: contextError } = await supabase.rpc('get_my_app_context', {
      target_slug: configuredOrganizationSlug,
    });

    if (contextError) {
      setError(contextError.message);
      setStatus('error');
      return;
    }

    const context = data as AppContextResponse;
    setProfile(context.profile);
    setMembership(context.membership);
    setOrganization(context.organization ? mapOrganization(context.organization) : null);

    if (context.access_granted) setStatus('active');
    else if (context.can_bootstrap) setStatus('needs-bootstrap');
    else setStatus('pending');
  }, []);

  const refreshContext = useCallback(async () => {
    if (!supabase) return;
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    await loadContext(data.session);
  }, [loadContext]);

  useEffect(() => {
    let mounted = true;

    if (isDemoMode) {
      setStatus('demo');
      return () => { mounted = false; };
    }
    if (!supabase) {
      setStatus('signed-out');
      return () => { mounted = false; };
    }

    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      void loadContext(data.session);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;
      setSession(nextSession);
      queueMicrotask(() => { void loadContext(nextSession); });
    });

    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [loadContext]);

  const signOut = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setMembership(null);
    setOrganization(null);
    setStatus(isDemoMode ? 'demo' : 'signed-out');
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    user: session?.user ?? null,
    profile,
    membership,
    organization,
    error,
    refreshContext,
    signOut,
  }), [error, membership, organization, profile, refreshContext, session?.user, signOut, status]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
